import {DxGridColumn} from "../ui-options/column/dx-grid-column";
import {DxGridOptions} from "../ui-options/dx-grid-options";
import {ColDef, GridOptions} from "ag-grid-community";
import {DxGridColumnDataType} from "../ui-options/column/dx-grid-column-data-type";
import {DxGridComponent} from "../../ui/components/dx-grid-component";
import {DxGridFilter} from "../filter/dx-grid-filter";
import {DxGridDataType} from "../dx-grid-data-type";
import {DxGridFilterOperationType} from "../filter/dx-grid-filter-operation-type";
import {DxNciDictionary} from "../../../dx-nci-core/domain/dx-nci-dictionary";


/**
 * Класс группирует вспомогательные static-методы, связанные с настрокой и
 * прикладным использованием комонента AgGrid.
 */
export class AgGridUtils {
  /**
   * Собирает объект настроек компонента AgGrid на основе прикланого объекта опции и контекста комопнета.
   * @param {DxGridOptions} options - опции комонента-обретки.
   * @param {DxGridComponent} context - контекст `this` компонета-обертки. Необходим для
   *                                    регистрации обработчиков и функций рендеринга.
   * @return {GridOptions} - объект настроек компонента AgGrid.
   */
  public static buildAgGridOptions(options: DxGridOptions, context: DxGridComponent): GridOptions {
    const agGridOptions: GridOptions = {};
    // Сценарий выделения строк в гриде.
    agGridOptions.rowSelection = options.allowMultiSelect ? 'multiple' : 'single';
    agGridOptions.rowDeselection = true;
    agGridOptions.suppressLoadingOverlay = false;
    agGridOptions.floatingFilter = options.showFiltersBar;
    agGridOptions.domLayout = options.autoHeight ? 'autoHeight' : 'normal';
    agGridOptions.headerHeight = options.headerHeight;
    agGridOptions.getRowHeight = options.getRowHeight;

    // Тип модели данных (по-умолчанию используется клиентская модель данных).
    if(options.useServerSideDataModel) {
      agGridOptions.rowModelType = 'infinite';
      agGridOptions.cacheBlockSize = options.infiniteScrollOptions.initialRowCount;
      agGridOptions.maxBlocksInCache = options.infiniteScrollOptions.maxBlocksInCache;
    }

    // Настройки пагинации
    if(options.allowPagination) {
      agGridOptions.pagination = true;
      agGridOptions.paginationPageSize = options.paginationOptions.pageSize;
      agGridOptions.paginationAutoPageSize = options.paginationOptions.useAutoPageSize;
      agGridOptions.suppressPaginationPanel = options.paginationOptions.suppressDefaultPaginationPanel;
    }

    /* Формирование настроек колонок и типов данных. */

    // Массив с объектами метаданных колонок
    const columnDefs: any[] = [];
    // Типы колонок представляют собой мапу
    const columnTypes: any = {};

    let columnDef: ColDef;
    options.columns.forEach(column => {
      columnDef = AgGridUtils.buildColumnDef(column, context);
      columnDef.editable = options.useInlineEdit && !column.disabled;
      columnDefs.push(columnDef);
      columnTypes[column.dataType.typeIdent] = AgGridUtils.buildColumnTypeDef(column.dataType, context);
    });

    agGridOptions.columnDefs = columnDefs;
    agGridOptions.columnTypes = columnTypes;
    agGridOptions.localeText = {
      page: 'Страница',
      of: 'из',
      to: 'по',
      loadingOoo: 'Загрузка...',
      noRowsToShow: 'Нет данных для отображения.'
    };
    return agGridOptions;
  }


  /**
   * Собирает объект настроек колонки компонента AgGrid на основе прикланого объекта опций и контекста комопнета.
   * @param {DxGridColumn} column - опции колоноки для комонента-обретки.
   * @param {DxGridComponent} grid - контекст `this` компонета-обертки. Необходим для
   *                                    регистрации обработчиков и функций рендеринга.
   * @return {ColDef} - объект настроек колонки для компонента AgGrid.
   */
  public static buildColumnDef(column: DxGridColumn, grid: DxGridComponent): ColDef {
    let columnDef: ColDef = {};
    // Наименование, идентификация и видимость колонки
    columnDef.headerName = column.title;
    columnDef.colId = column.ident;
    columnDef.field = column.property;
    columnDef.hide = !column.visible;

    // Минимальная, максимальная ширина + растягиваемость
    columnDef.minWidth = column.width;
    columnDef.maxWidth = column.maxWidth;
    columnDef.resizable = column.resizable;
    columnDef.pinned = column.pinned;

    // Настроки сортировки по колонке
    columnDef.sortable = column.sortable;
    if (column.sortable) columnDef.unSortIcon = true;
    if (column.sortDirection) columnDef.sort = column.sortDirection;

    // Настроки фильтрации по колонке
    columnDef.filter = column.filterable;
    // Подавление меню фильтрации в ячейке заголовка
    columnDef.suppressMenu = true;
    // Подавление кнопки раскрытия филтрации для плавающих фильтров
    columnDef.floatingFilterComponentParams = { suppressFilterButton: true };
    // Возможность перемещения колонки
    columnDef.suppressMovable = !column.movable;
    // Автоматическая подготка высоты строк по содержимому колонки
    columnDef.autoHeight = column.autoHeight;
    if (column.autoHeight) columnDef.cellStyle =  { "white-space": "normal" };

    columnDef.cellClass = column.cellClass;

    if (column.hasTooltip) columnDef.headerTooltip = column.tooltip;

    // Определяем способ выравнивания в заголовках колонок
    switch (column.headerJustify) {
      case 'center': columnDef.headerClass = 'dx-grid__header--justify-center'; break;
      case 'right' : columnDef.headerClass = 'ag-numeric-header'; break;
    }

    // Поключение кастомныых редакторов, ренедереров и фильтров (в зависимости от типа данных)
    AgGridUtils.setupCellEditor(columnDef, column, grid);
    AgGridUtils.setupCellRender(columnDef, column, grid);
    AgGridUtils.setupFloatingFilter(columnDef, column, grid);
    return columnDef;
  }


  /**
   * Собирает тип данных колонки компонента AgGrid на основе прикланого объекта опций и контекста комопнета.
   * @param {DxGridColumnDataType} dataType - опции типа данных колоноки для комонента-обретки.
   * @param {DxGridComponent} context - контекст `this` компонета-обертки. Необходим для
   *                                    регистрации обработчиков и функций рендеринга.
   * @return {ColDef} - тип данных колонки для компонента AgGrid.
   */
  public static buildColumnTypeDef(dataType: DxGridColumnDataType, context: DxGridComponent): ColDef {
    return {};
  }


  /**
   * Конвертирует `sortModel` комопнента AgGrid мапу [атрибут сущности - направление сортировки].
   * @param {DxGridColumn[]} columns - метаданные колонок.
   * @param {any} sortModel - модель сортировки комопнента AgGrid.
   * @return {Map<string, string>} - мапа [атрибут сущности - направление сортировки].
   */
  public static prepareSorts(columns: DxGridColumn[], sortModel: any[]): Map<string, string> {
    const sorts: Map<string, string> = new Map();
    let column: DxGridColumn;

    sortModel.forEach(item => {
      column = columns.find(col => col.ident === item.colId);
      sorts.set(column.property, item.sort);
    });

    return sorts;
  }


  /**
   * Конвертирует `filterModel` комопнента AgGrid мапу [атрибут сущности - фильтр].
   * @param {DxGridColumn[]} columns - метаданные колонок.
   * @param {any} filterModel - модель фильтрации комопнента AgGrid.
   * @param {Map<string, DxNciDictionary>} dicts - опциональная мапа справочников.
   * @return {Map<string, DxGridFilter>} - мапа [атрибут сущности - фильтр].
   */
  public static prepareFilters(columns: DxGridColumn[], filterModel: any, dicts?: Map<string, DxNciDictionary>): Map<string, DxGridFilter> {
    const filters: Map<string, DxGridFilter> = new Map();
    const colKeys = Object.keys(filterModel);

    let column: DxGridColumn;
    let filter: DxGridFilter;
    let value: any;
    let operation: DxGridFilterOperationType;
    let dict: DxNciDictionary;
    let dataType: DxGridDataType;
    colKeys.forEach(colKey => {
      column = columns.find(col => col.ident === colKey);
      value = filterModel[colKey].filter;
      operation = AgGridUtils.dispatchOperationType(filterModel[colKey].type);
      dataType = AgGridUtils.dispatchFilterDataType(column.dataType.typeIdent);

      // TODO: Прокидывание справочника в фильтр сделано только для возможности
      // TODO: клиенсткой фильтрации по колонкам-справочникам. Пока не реализованы:
      // TODO: 1. Бэкэнд :)
      // TODO: 2. Фильтр-выпадайка с чекбокссами для фильтрации справочников.
      // TODO: По-идее, справочник должн фильтроватся по условию equals для id элементов
      // TODO: Убрать это в последующем!
      if(column.dataType.isDictionary && dicts && dicts.has(column.dataType.dictIdent)) {
        dict = dicts.get(column.dataType.dictIdent);
      }

      filter = new DxGridFilter(dataType, operation, [value], dict);
      filters.set(column.property, filter);
    });
    return filters;
  }


  /**
   * Конвертирует `filterModel` комопнента AgGrid мапу [атрибут сущности - фильтр].
   * @param {DxGridColumn[]} columns - метаданные колонок.
   * @param {Map<string, DxGridFilter>} filters - мапа фильтров.
   * @return {any} - модель фильтрации в формате комопнента AgGrid.
   */
  public static prepareFilterModel(columns: DxGridColumn[], filters: Map<string, DxGridFilter>, dicts?: Map<string, DxNciDictionary>): any {
    const filterModel: any = {};
    let filter: any;
    let filterVal: any;
    let column: DxGridColumn;
    filters.forEach((value, key) => {
      column = columns.find(item => item.ident === key);
      if(column) {
        // TODO: Пока рассматривается только единичная отметка!!!
        filterVal = value.values[0];

        // TODO: Преобразование ниже для колонки-справочника делается только для поддержки
        // TODO: клиентской фльтрации колонок-справочников. Фильтрация справочников должна идти
        // TODO: по условию equals для id элементов. Если не сделать это преобразование,
        // TODO: то в фильтре будет отображаться id
        // TODO: Пока не реализованы:
        // TODO: 1. Бэкэнд :)
        // TODO: 2. Фильтр-выпадайка с чекбокссами для фильтрации справочников.
        // TODO: Убрать это в последующем!
        if(column.dataType.isDictionary && dicts && dicts.has(column.dataType.dictIdent)) {
          const dict = dicts.get(column.dataType.dictIdent);
          const dictElem = dict.elementIndex.get(value.values[0]);
          filterVal = dictElem.name;
        }

        filter = {
          colId: column.ident,
          filterType: 'text',
          type: AgGridUtils.dispatchFilterType(value.operation),
          filter: filterVal
        };
        filterModel[column.ident] = filter;
      }
    });
    return filterModel;
  }


  /**
   * Мапит прикладной тип операции на тип логической операции в модели фильтрации комопонента AgGrid.
   * @param {DxGridFilterOperationType} operation - прикладной тип логической операции.
   */
  public static dispatchFilterType(operation: DxGridFilterOperationType): string {
    // TODO: Добавить остальные типы из спецификации AgGrid
    switch (operation) {
      case DxGridFilterOperationType.CONTAINS:
        return 'contains';
      case DxGridFilterOperationType.EQUALS:
        return 'equals';
      default:
        return 'contains';
    }
  }


  /**
   * Мапит тип логической операции в модели фильтрации комопонента AgGrid на прикладной тип.
   * @param {string} type - тип логической операции в модели фильтрации комопонента AgGrid.
   */
  public static dispatchOperationType(type: string): DxGridFilterOperationType {
    // TODO: Добавить остальные типы из спецификации AgGrid
    switch (type) {
      case 'contains':
        return DxGridFilterOperationType.CONTAINS;
      case 'equals':
        return DxGridFilterOperationType.EQUALS;
      default:
        return DxGridFilterOperationType.CONTAINS;
    }
  }


  public static dispatchFilterDataType(type: string): DxGridDataType {
    switch (type) {
      case DxGridDataType.TEXT:
        return DxGridDataType.TEXT;
      case DxGridDataType.LARGE_TEXT:
        return DxGridDataType.LARGE_TEXT;
      case DxGridDataType.LINK:
        return DxGridDataType.LINK;
      case DxGridDataType.INT:
        return DxGridDataType.INT;
      case DxGridDataType.DOUBLE:
        return DxGridDataType.DOUBLE;
      case DxGridDataType.DICTIONARY:
        return DxGridDataType.DICTIONARY;
      case DxGridDataType.CURRENCY:
        return DxGridDataType.CURRENCY;
      case DxGridDataType.DATE:
        return DxGridDataType.DATE;
      case DxGridDataType.TIME:
        return DxGridDataType.TIME;
      case DxGridDataType.DATE_TIME:
        return DxGridDataType.DATE_TIME;
      case DxGridDataType.CHECKBOX:
        return DxGridDataType.CHECKBOX;
      default:
        return DxGridDataType.TEXT;
    }
  }


  public static setupCellEditor(columnDef: ColDef, column: DxGridColumn, grid: DxGridComponent): void {
    if(grid.cellEditors.has(column.dataType.typeIdent)) {
      columnDef.cellEditorFramework = grid.cellEditors.get(column.dataType.typeIdent);
    } else {
      switch (column.dataType.typeIdent) {
        case DxGridDataType.TEXT:
          columnDef.cellEditor = 'agTextCellEditor';
          break;
        case DxGridDataType.LARGE_TEXT:
          columnDef.cellEditor = 'agLargeTextCellEditor';
          break;
        default:
          columnDef.cellEditor = 'agTextCellEditor';
          break;
      }
    }
    columnDef.cellEditorParams = {columnOptions: column};
  }


  public static setupCellRender(columnDef: ColDef, column: DxGridColumn, grid: DxGridComponent): void {
    if (column.cellRendererFramework) {
      columnDef.cellRendererFramework = column.cellRendererFramework;
      return;
    }
    if (grid.cellRenders.has(column.dataType.typeIdent)) {
      columnDef.cellRendererFramework = grid.cellRenders.get(column.dataType.typeIdent);

      const params: any = {};
      params.columnOptions = column;
      if (DxGridDataType.DICTIONARY === column.dataType.typeIdent) {
        // ВНИМАНИЕ!!!
        // Ссылка на грид для рендеринга справочников передается напрямую чтобы,
        // в случае использования серверной модели данных (асинхронной загрузки справочников),
        // избежать выноса мозка с синхронизацией момента окончания загрузки справочников и момента
        // инициализации настроек грида.
        // Просто передаем в рендер ссылку на грид. Рендер на этапе своей нициализации через геттер
        // достанет справочники и найдет (а может и нет:)) нужный.
        // Важно что к моменту инициализации рендера данные справочников уже будет гарантировано получены.
        // Этот нюанс сильно упрошает жизнь и код.
        params.grid = grid;
      }
      columnDef.cellRendererParams = params;
    } else {
      // Если специальный рендер не определен, то
      // оставляем рендер по-умолчанию.
    }
  }

  public static setupFloatingFilter(columnDef: ColDef, column: DxGridColumn, grid: DxGridComponent): void {
    if (grid.floatingFilters.has(column.dataType.typeIdent)) {
      columnDef.floatingFilterComponentFramework = grid.floatingFilters.get(column.dataType.typeIdent);

      const params: any = {suppressFilterButton: true};
      params.columnOptions = column;
      if(DxGridDataType.DICTIONARY === column.dataType.typeIdent) {
        // ВНИМАНИЕ!!!
        // Ссылка на грид для рендеринга справочников передается напрямую чтобы,
        // в случае использования серверной модели данных (асинхронной загрузки справочников),
        // избежать выноса мозка с синхронизацией момента окончания загрузки справочников и момента
        // инициализации настроек грида.
        // Просто передаем в рендер ссылку на грид. Рендер на этапе своей нициализации через геттер
        // достанет справочники и найдет (а может и нет:)) нужный.
        // Важно что к моменту инициализации рендера данные справочников уже будет гарантировано получены.
        // Этот нюанс сильно упрошает жизнь и код.
        params.grid = grid;
      }
      columnDef.floatingFilterComponentParams = params;
    } else {
      // Если специальный рендер не определен, то
      // оставляем рендер по-умолчанию.
    }

  }

}
